import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { TranslocoService } from '@jsverse/transloco';

import { PasswordRule, SemantriaAccount } from 'app/providers/bridge-api/account.service';
import { AlertService } from 'app/services/alert.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';

@EnableClassTelemetry()
@Component({
  selector: 'app-password-rules-check',
  templateUrl: './password-rules-check.component.html',
  styleUrls: ['./password-rules-check.component.scss'],
  standalone: false,
})
export class PasswordRulesCheckComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  _password = '';

  @Input() set password(value: string) {
    this._password = value;
    setTimeout(() => {
      this.validChange.emit(this.allValid());
    }, 50);
  }

  @Input() valid!: boolean | string;
  @Output() validChange = new EventEmitter<boolean>();
  rules: PasswordRule[] = [];
  isLoading = false;

  constructor(
    private alertService: AlertService,
    private accountService: SemantriaAccount,
    private translocoService: TranslocoService
  ) {}

  async ngOnInit() {
    this.isLoading = true;
    try {
      const rules = await this.accountService.getPasswordRules();
      this.rules = rules.rules.filter((rule) => {
        return (
          rule.rule_type !== 'password_history_limit' && rule.rule_type !== 'password_expiration'
        );
      });
    } catch (error) {
      this.alertService.showErrorToast(
        this.translocoService.translate('password-check-component.failed-to-get-rules'),
        error
      );
    } finally {
      this.isLoading = false;
    }
  }

  public isValid(value: string): boolean {
    const invalid = this.rules.filter((rule) => !this.validForRule(rule, value));
    return invalid.length === 0;
  }

  public allValid(): boolean {
    return this.isValid(this._password);
  }

  public validForRule(rule: PasswordRule, password: string = undefined): boolean {
    if (!password) {
      password = this._password;
    }
    switch (rule.rule_type) {
      case 'min_length':
        return password.length >= +rule.value;
      case 'uppercase_chars':
        return [...password.matchAll(/[A-Z]/g)].length >= +rule.value;
      case 'lowercase_chars':
        return [...password.matchAll(/[a-z]/g)].length >= +rule.value;
      case 'digits':
        return [...password.matchAll(/\d/g)].length >= +rule.value;
      case 'special_chars':
        return [...password.matchAll(/[!@#\$%\^\&*\)\(+=._-]/g)].length >= +rule.value;
    }

    return false;
  }

  public labelForRuleType(ruleType: string): string {
    switch (ruleType) {
      case 'min_length':
        return this.translocoService.translate('password-check-component.labels.characters');
      case 'uppercase_chars':
        return this.translocoService.translate(
          'password-check-component.labels.uppercase-characters'
        );
      case 'lowercase_chars':
        return this.translocoService.translate(
          'password-check-component.labels.lowercase-characters'
        );
      case 'digits':
        return this.translocoService.translate('password-check-component.labels.numbers');
      case 'special_chars':
        return this.translocoService.translate('password-check-component.labels.symbols');
    }
    return ruleType;
  }
}
